import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { routes, getLanguageFromPath, getAlternateUrl, getRoute } from '../config/routes';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const didInit = useRef(false);

  // Language is derived from the URL (source of truth)
  const language = getLanguageFromPath(location.pathname);

  // One-time browser language detection on first root visit
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    if (location.pathname !== '/') return;

    const savedLang = localStorage.getItem('pikAgencyLang');
    if (savedLang === 'de') {
      navigate('/de', { replace: true });
      return;
    }
    if (!savedLang) {
      const browserLang = (navigator.language || '').toLowerCase();
      if (browserLang.startsWith('de')) {
        navigate('/de', { replace: true });
      }
    }
  }, [location.pathname, navigate]);

  // Keep localStorage in sync with URL language
  useEffect(() => {
    localStorage.setItem('pikAgencyLang', language);
  }, [language]);

  const switchLanguage = (lang) => {
    if (lang === language) return;
    const targetPath = getAlternateUrl(location.pathname, lang);
    localStorage.setItem('pikAgencyLang', lang);
    navigate(targetPath);
  };

  const value = useMemo(
    () => ({
      language,
      switchLanguage,
      paths: routes[language],
      buildPath: (key, params) => getRoute(key, language, params)
    }),
    
    [language, location.pathname]
  );

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
};
